import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';

// Auth pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// Main pages
import DashboardPage from './pages/DashboardPage';
import BranchesPage from './pages/BranchesPage';
import UsersPage from './pages/UsersPage';
import DataStructuresPage from './pages/DataStructuresPage';
import RecordsPage from './pages/RecordsPage';
import ArchivedRecordsPage from './pages/ArchivedRecordsPage';
import RecordDetailPage from './pages/RecordDetailPage';
import NewRecordPage from './pages/NewRecordPage';
import LandingPage from './pages/LandingPage';
import RemindersPage from './pages/RemindersPage';
import TemporaryLinksPage from './pages/TemporaryLinksPage';
import TemporaryAccessPage from './pages/TemporaryAccessPage';
import ReportsPage from './pages/ReportsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AllocationRulesPage from './pages/AllocationRulesPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';

// Settings pages
import StatusesPage from './pages/settings/StatusesPage';
import WebhooksPage from './pages/settings/WebhooksPage';
import ThemesPage from './pages/settings/ThemesPage';
import SMTPPage from './pages/settings/SMTPPage';
import IPWhitelistPage from './pages/settings/IPWhitelistPage';
import AppSettingsPage from './pages/settings/AppSettingsPage';
import AuditLogsPage from './pages/settings/AuditLogsPage';

import './App.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/temp-access/:token" element={<TemporaryAccessPage />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout><DashboardPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/branches" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><BranchesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/users" element={
            <ProtectedRoute allowedRoles={['super_admin', 'branch_admin']}>
              <Layout><UsersPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/data-structures" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><DataStructuresPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/records" element={
            <ProtectedRoute>
              <Layout><RecordsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/records/archived" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><ArchivedRecordsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/records/new" element={
            <ProtectedRoute>
              <Layout><NewRecordPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/records/:id" element={
            <ProtectedRoute>
              <Layout><RecordDetailPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/reminders" element={
            <ProtectedRoute>
              <Layout><RemindersPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/temporary-links" element={
            <ProtectedRoute>
              <Layout><TemporaryLinksPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/reports" element={
            <ProtectedRoute allowedRoles={['super_admin', 'branch_admin']}>
              <Layout><ReportsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/leaderboard" element={
            <ProtectedRoute allowedRoles={['super_admin', 'branch_admin']}>
              <Layout><LeaderboardPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/allocation-rules" element={
            <ProtectedRoute allowedRoles={['super_admin', 'branch_admin']}>
              <Layout><AllocationRulesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout><ProfilePage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/notifications" element={
            <ProtectedRoute>
              <Layout><NotificationsPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Settings routes */}
          <Route path="/settings/statuses" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><StatusesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings/webhooks" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><WebhooksPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings/themes" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><ThemesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings/smtp" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><SMTPPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings/ip-whitelist" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><IPWhitelistPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings/app" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><AppSettingsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings/audit-logs" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Layout><AuditLogsPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Landing & Redirects */}
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
